/* global google*/

/**
 *
 *
 * @attr {string} googleplace-callback - REQUIRED, string path to callback that maps the data to the model
 * @attr {boolean} googleplace-resolve-to-street-address - optional paramater that resolbes the input field
 * to the street address instead the full description of the location
 *
 * @param {*} $compile
 * @param {*} $q
 */

import { get } from 'lodash';

export default function($compile, $q, $log, $http) {
  'ngInject';

  const autoCompleteService = new google.maps.places.AutocompleteService();
  const dummyMap = new google.maps.Map(document.createElement('div'));
  const placesService = new google.maps.places.PlacesService(dummyMap);

  return {
    priority: 1001,
    terminal: true,
    restrict: 'A',
    requires: 'googleplaceCallback',
    scope: false,
    link: (scope, element, attrs) => {
      // remove the component name to avoid stack overflow from reapplication of the directive
      element.removeAttr('googleplace');

      // apply the uib-typeahead directives
      element.attr('uib-typeahead', 'place as place.description for place in getAutocomplete($viewValue)');
      element.attr('typeahead-on-select', 'placeSelected($item)');
      element.attr('typeahead-editable', true);
      element.attr('typeahead-focus-first', false);

      // resolve the field to the street address vs the entire address
      if (attrs.googleplaceResolveToStreetAddress) {
        element.attr('typeahead-input-formatter', 'inputFormatter($model)');
      }

      // turn off browser's autocomplete on the field
      element.attr('autocomplete', 'no');

      const getTimeZoneId = ({ lat, lng }) => {
        const timestamp = Math.round(Date.now() / 1000).toString();
        const url = `https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${timestamp}&key=AIzaSyCa6KYuzTcwkhST7n3PuxLOv4NKILCBqcg`;

        return $http({
          method: 'GET',
          url: url
        });
      };

      /**
       * The parent scope holds the function that maps the location props to the model. We couldn't figure
       * out how to get the scope to resolve to both this directive and the uib-typeahead directive properly,
       * so we set scope to false and use the parent scope in the directive. As a result, we can't get a
       * function from the element attributes, and have to resolve it here.
       *
       * This does some checks to see that we have a callback property, as well as catches errors from the
       * string path function.
       *
       * @returns {Function} - Function provided by the parent scope that will accept an object that
       * includes the details and placeid props from the original autocomplete object, as well as, optionally,
       * our location props from the secondary call to the place details function. It also accepts, as a second
       * prop, a function that when invoked will update the scope.
       *
       */
      const mapLocationPropsToModel = (() => {
        try {
          if (!attrs.googleplaceCallback) {
            throw new Error('googleplace-callback property is required');
          }

          return getPropFromStringPath(scope.$parent, attrs.googleplaceCallback);
        } catch (err) {
          $log.error(err);
        }
      })();

      /**
       * Takes a base object, and gets a property from a period separated string path
       *
       * @param {Object} obj - object to get the prop from
       * @param {string} strPath - period separated path to the prop
       *
       * @returns {*} prop - nested prop from the object
       *
       * @throws {Error} - If the path doesn't resolve on the scope
       */
      function getPropFromStringPath(obj, strPath) {
        return strPath.split('.').reduce((acc, prop) => {
          if (acc.hasOwnProperty(prop)) {
            return acc[prop];
          }
          throw new Error('google-callback method is not defined on the scope');
        }, obj);
      }

      /**
       * Takes the string from the input or the google place object from typeahead. If the latter, uses a prop
       * that gives the street address to populate the field, preventing a flash of the whole string going into
       * the input before being reset to the address.
       *
       * @param {string | Object} model - either the text in the input or the place object from google maps
       */
      scope.inputFormatter = model => {
        if (!model) return undefined;

        if (typeof model === 'string') return model;

        return get(model, 'structured_formatting.main_text', undefined);
      };

      /**
       * Queries the Google Place API with the search string and returns a promise that resolves to the
       * response object
       *
       * @param {string} input - whatever the user is typing
       *
       * @returns {Promise} - resolves to google response object, which is an array of places
       */
      scope.getAutocomplete = input => {
        return $q((resolve, reject) => {
          try {
            autoCompleteService.getQueryPredictions({ input }, resp => {
              resolve(resp);
            });
          } catch (err) {
            $log.error(err);
          }
        });
      };

      /**
       * When a suggestion is selected, takes the place object, and makes call to the place details api
       * to get the address components. If that's good, it then reaches out to the Timezone API. It then
       * maps whatever properties it gets to our address interface, and then sends them to the provided
       *  callback function that maps them to the model, and includes the apply function for that function
       * to refresh the parent component.
       *
       * @param {Object} placeObject - Google autocomplete place object
       */
      scope.placeSelected = ({ description, place_id }) => {
        const autoCompleteData = {
          googleLocationDescription: description,
          googlePlaceId: place_id
        };

        // forces an update of the model if it's not already doing so
        const applyModel = () => {
          if (!scope.$$phase) {
            scope.$apply();
          }
        };

        try {
          placesService.getDetails(
            {
              placeId: place_id,
              fields: ['address_components', 'geometry']
            },
            (resp, status) => {
              if (status === 'OK') {
                const locationObj = mapPlaceDetailsToLocation(resp);
                getTimeZoneId(locationObj).then(
                  resp => {
                    if (resp.status === 200 && resp.data != null && resp.data.timeZoneId != null) {
                      const { timeZoneId } = resp.data;
                      mapLocationPropsToModel({ ...locationObj, ...autoCompleteData, timeZoneId }, applyModel);
                    } else {
                      mapLocationPropsToModel({ ...locationObj, ...autoCompleteData }, applyModel);
                    }
                  },
                  () => {
                    mapLocationPropsToModel({ ...locationObj, ...autoCompleteData }, applyModel);
                  }
                );
              } else {
                mapLocationPropsToModel({ ...autoCompleteData }, applyModel);
              }
            }
          );
        } catch (err) {
          $log.error(err);
        }
      };

      /**
       * Takes a response object from Google Place Details and maps it to our location fields. Fields
       * that aren't present are set to null.
       *
       * This function is a best guess on how to map the components based on a few tries. Here are
       * some addresses tested:
       * - 25 Old Pump Rd, Jericho VT 05452
       * - 111 S Jackson St, Seattle WA 98104, United States
       * - 110 Kippax St, Surrey Hills NSW, Australia
       * - 48 Percy Place, Ballsbridge, Dublin 4 Ireland
       *
       * @param {Object} placeDetails - Google place details response object
       *
       * @returns {Object} - object with our address fields
       */
      function mapPlaceDetailsToLocation(placeDetails) {
        const { address_components, geometry } = placeDetails;

        const locationObj = {
          address: '',
          addressTwo: '',
          city: '',
          state: '',
          zip: '',
          country: '',
          lat: geometry.location.lat(),
          lng: geometry.location.lng()
        };

        return address_components.reduce((acc, cur) => {
          switch (cur.types[0]) {
            case 'street_number':
              acc.address = cur.long_name;
              break;
            case 'route':
              acc.address = acc.address === '' ? cur.long_name : `${acc.address} ${cur.long_name}`;
              break;
            case 'locality':
            case 'administrative_area_level_3':
              acc.city = acc.city ? `${acc.city} ${cur.long_name}` : cur.long_name;
              break;
            case 'administrative_area_level_1':
              acc.state = cur.short_name;
              break;
            case 'postal_code':
              acc.zip = cur.long_name;
              break;
            case 'country':
              acc.country = cur.long_name;
              break;
          }
          return acc;
        }, locationObj);
      }

      // force a compile with the updated element
      $compile(element)(scope);
    }
  };
}



// WEBPACK FOOTER //
// ./src/app/directives/googleplace/index.js